import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Text, Box, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import CountDownTimer from '../../../countdown-timer'
import CountDownTimerVariant from '../../../countdown-timer/partials/countDownTimerVariant'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const CountdownTimerBanner = ({
    countdownHeadlineText,
    countdownHeadlineTextTag = 'h2',
    countdownTextColor = 'white',
    countdownFontFamily = 'caslon',
    desktopAlignment,
    mobileAlignment,
    backgroundColor = 'yellow.600',
    countdownSize,
    useTimerAsHeadlineOnMobile,
    ctaText,
    ctaUrl,
    ctaTextColor,
    hideCTAOnMobile,
    datetimepicker = {},
    hour = '00',
    minute = '00',
    second = '00',
    typeId,
    name
}) => {
    const pdTagManager = usePdTagManager()
    const appOrigin = useAppOrigin()
    const textColor = countdownTextColor === 'white' ? 'white' : 'black'
    const ctacolor = ctaTextColor === 'white' ? 'white' : 'black'
    const isLargeCountDown = countdownSize === 'large'
    const timerAsHeadlineOnMobile = isLargeCountDown && useTimerAsHeadlineOnMobile
    const timerBoxStyles = {
        backgroundColor: backgroundColor,
        textAlign: 'center',
        padding: {base: '15px', lg: '15px 15px 15px 40px'},
        margin: '0 auto',
        display: 'flex',
        minWidth: '200px',
        width: '100%',
        alignItems: 'center',
        flexDirection: {base: 'column', lg: 'row'},
        position: 'relative',
        gap: {base: '5px', lg: '0px'},
        color: textColor,
        justifyContent: {base: mobileAlignment || 'center', lg: desktopAlignment || 'center'},
        flex: 1
    }

    const ctaProps = {
        href: ctaUrl,
        title: ctaText,
        padding: {base: '0px', lg: '0 20px'},
        textDecoration: 'underline',
        color: ctacolor,
        fontFamily: 'inter',
        display: 'inline-block',
        pos: {
            base: hideCTAOnMobile ? 'absolute' : 'relative',
            lg: desktopAlignment === 'right' ? 'relative' : 'absolute'
        },
        right: {lg: desktopAlignment === 'right' ? '0' : '15px'},
        width: {base: '100%', lg: 'auto'},
        height: hideCTAOnMobile ? {base: '100%', lg: 'auto'} : 'auto',
        visibility: hideCTAOnMobile ? {base: 'hidden', lg: 'visible'} : 'visible',
        textAlign: {base: mobileAlignment, lg: 'inherit'}
    }

    // date and time object
    const targetDateConfig = {
        dateVal: datetimepicker?.value,
        hour,
        minute,
        second
    }

    // determine the order of the timer based on configuration
    const getTimerOrder = () => {
        if (timerAsHeadlineOnMobile) {
            return {base: 2, lg: 3}
        }
        if (isLargeCountDown) {
            return {base: 1, lg: 3}
        }
        return {base: 3, lg: 3}
    }

    const countDownBannerStyles = {...CountDownTimerVariant[countdownSize]}

    countDownBannerStyles.headline = {
        ...countDownBannerStyles.headline,
        color: textColor,
        fontFamily: countdownFontFamily
    }

    countDownBannerStyles.eyeBrow = {
        ...countDownBannerStyles.eyeBrow,
        color: textColor,
        fontFamily: countdownFontFamily
    }

    // extend the styles base on configuration
    countDownBannerStyles.box = {
        ...countDownBannerStyles.box,
        color: textColor,
        fontFamily: countdownFontFamily,
        order: getTimerOrder
    }

    const getHeadlineStyles = () => {
        let style = {
            order: 1,
            ...countDownBannerStyles.headline
        }

        if (isLargeCountDown && !timerAsHeadlineOnMobile) {
            return (style = {
                order: {base: 2, lg: 1},
                ...countDownBannerStyles.headline
            })
        }
        if (timerAsHeadlineOnMobile) {
            return (style = {
                order: 1,
                ...countDownBannerStyles.eyeBrowTimer,
                color: textColor,
                fontFamily: countdownFontFamily
            })
        }
        return style
    }

    const mobileLargeAlignment = (alignment) => {
        if (alignment === 'left') return 'flex-start'
        if (alignment === 'right') return 'flex-end'
        return 'center'
    }

    useEffect(() => {
        pdTagManager.viewComponent(typeId, name)
    }, [])

    return (
        <>
            {countdownHeadlineText && (
                <Box {...timerBoxStyles} data-type={countdownSize}>
                    {/* large countdown size has eyebrow of either Headline or Timer */}
                    <Box
                        {...CountDownTimerVariant.commonStyles}
                        width={{base: '100%', lg: 'auto'}}
                        justifyContent={{
                            base: mobileAlignment || 'center',
                            lg: desktopAlignment || 'center'
                        }}
                        alignItems={{base: mobileLargeAlignment(mobileAlignment), lg: 'inherit'}}
                        flexDirection={isLargeCountDown ? {base: 'column', lg: 'row'} : 'row'}
                    >
                        {/* Large Countdown,
                            - Headline is either text or timer 
                            - Eybrow is either text or timer 
                         */}
                        {isLargeCountDown && (
                            <>
                                <Text as={countdownHeadlineTextTag} {...getHeadlineStyles()}>
                                    {countdownHeadlineText}
                                </Text>
                                {datetimepicker?.value && (
                                    <>
                                        {/* dash is hidden on large countdown mobile */}
                                        <Text
                                            as="span"
                                            order="2"
                                            display={{base: 'none', lg: 'inline-block'}}
                                            {...countDownBannerStyles.headline}
                                        >
                                            -
                                        </Text>
                                        <CountDownTimer
                                            as={countdownHeadlineTextTag}
                                            targetDate={targetDateConfig}
                                            variantStyles={countDownBannerStyles}
                                            type={timerAsHeadlineOnMobile ? 'default' : 'eyeBrow'}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        {!isLargeCountDown && (
                            <>
                                <Text
                                    order="1"
                                    as={countdownHeadlineTextTag}
                                    {...countDownBannerStyles.headline}
                                >
                                    {countdownHeadlineText}
                                </Text>

                                {datetimepicker?.value && (
                                    <>
                                        {/* dash is hidden on large countdown mobile */}
                                        <Text
                                            as="span"
                                            order="2"
                                            {...countDownBannerStyles.headline}
                                        >
                                            -
                                        </Text>
                                        <CountDownTimer
                                            targetDate={targetDateConfig}
                                            variantStyles={countDownBannerStyles}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                    {ctaText && ctaUrl && (
                        <Link
                            {...CountDownTimerVariant[countdownSize].cta}
                            {...ctaProps}
                            onClick={() => {
                                pdTagManager.selectComponent({
                                    componentId: typeId,
                                    componentName: name,
                                    titleName: countdownHeadlineText,
                                    linkUrl:
                                        ctaUrl?.indexOf('http') !== -1
                                            ? ctaUrl
                                            : `${appOrigin}${ctaUrl}`
                                })
                            }}
                        >
                            {ctaText}
                        </Link>
                    )}
                </Box>
            )}
        </>
    )
}

CountdownTimerBanner.propTypes = {
    countdownHeadlineText: PropTypes.string,
    countdownHeadlineTextTag: PropTypes.string,
    countdownTextColor: PropTypes.string,
    countdownFontFamily: PropTypes.string,
    desktopAlignment: PropTypes.string,
    mobileAlignment: PropTypes.string,
    backgroundColor: PropTypes.string,
    countdownSize: PropTypes.string,
    useTimerAsHeadlineOnMobile: PropTypes.bool,
    ctaText: PropTypes.string,
    ctaUrl: PropTypes.string,
    ctaTextColor: PropTypes.string,
    hideCTAOnMobile: PropTypes.bool,
    datetimepicker: PropTypes.object,
    hour: PropTypes.string,
    minute: PropTypes.string,
    second: PropTypes.string,
    typeId: PropTypes.string,
    name: PropTypes.any
}

export default CountdownTimerBanner
